import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { LANGUAGES, findLanguage } from "../../constants/languages";
import { useDismiss } from "../../hooks/useDismiss";

const useCurrentLanguage = () => {
  const { t, i18n } = useTranslation();
  // `resolvedLanguage` is the bundle actually rendering; `language` is what
  // the detector found, which may be one we don't ship.
  const current = findLanguage(i18n.resolvedLanguage || i18n.language);
  return { t, i18n, current };
};

/**
 * Globe button that opens the language list — the site header, and the
 * dashboard's top bar on a phone.
 *
 * `.dropdown` + `.bi-globe` are the e2e hooks for the site header's selector.
 * `triggerClassName` carries the whole look of the button; the base below
 * deliberately sets nothing a caller might want to change, because a colliding
 * utility appended to a shared class does nothing.
 */
export function LanguageMenu({ triggerClassName = "" }) {
  const { t, i18n, current } = useCurrentLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);

  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="dropdown relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className={`inline-flex cursor-pointer items-center gap-1.5 transition ${triggerClassName}`}
      >
        <i className="bi bi-globe text-base" aria-hidden="true" />
        <span className="sr-only">{t("Menu.Language")}</span>
        <span>{current.short}</span>
        <i
          className={`bi bi-chevron-down hidden text-xs transition-transform sm:inline-block ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <ul className="absolute right-0 top-full z-10 mt-2 min-w-44 overflow-hidden rounded-xl border border-ink-200 bg-white py-1 shadow-card-hover">
          {LANGUAGES.map((l) => {
            const selected = l.code === current.code;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  lang={l.code}
                  onClick={() => choose(l.code)}
                  aria-current={selected ? "true" : undefined}
                  className={`flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-brand-50 hover:text-brand-700 ${
                    selected ? "font-semibold text-brand-700" : "text-ink-700"
                  }`}
                >
                  <span>{l.label}</span>
                  {selected && (
                    <i className="bi bi-check2 text-base" aria-hidden="true" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

LanguageMenu.propTypes = {
  triggerClassName: PropTypes.string,
};

/**
 * "Language  [EN | RO]" as one row — for a menu that is already open, where a
 * second dropdown would be one click too many for two choices.
 */
export function LanguageToggle() {
  const { t, i18n, current } = useCurrentLanguage();

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-2 text-sm text-ink-600">
        <i className="bi bi-globe text-base text-ink-400" aria-hidden="true" />
        {t("Menu.Language")}
      </span>
      <div
        role="group"
        aria-label={t("Menu.Language")}
        className="inline-flex rounded-lg bg-ink-100 p-0.5"
      >
        {LANGUAGES.map((l) => {
          const selected = l.code === current.code;
          return (
            <button
              key={l.code}
              type="button"
              title={l.label}
              aria-pressed={selected}
              onClick={() => i18n.changeLanguage(l.code)}
              className={`cursor-pointer rounded-md px-2.5 py-1 text-xs font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600 ${
                selected
                  ? "bg-white text-brand-700 shadow-card"
                  : "text-ink-500 hover:text-ink-900"
              }`}
            >
              {l.short}
            </button>
          );
        })}
      </div>
    </div>
  );
}
